/**
 * The warm-up's diagrams.
 *
 * One component per `figure:` name a mission may use; the engine validates
 * the name against the same list (packages/engine/src/missions.ts,
 * TUTORIAL_FIGURES), so a figure that is missing here is a build failure
 * rather than an empty box on a learner's screen.
 *
 * Each figure draws the mechanism the card is about and animates once, when
 * its card arrives. Nothing here loops, and everything settles the moment a
 * viewer has asked for reduced motion.
 *
 * They are grouped by what they are claiming rather than split one to a file,
 * because the figures in a group share a drawing and are only worth reading
 * against each other: `shell` is where a command acts from and where its
 * output goes, `trees` is where a change physically is, `history` is the shape
 * of the commit graph, `collab` is other people's work, `recovery` is finding
 * something you cannot see, and `debug` is reading output you did not write.
 * The stage they are all drawn on, and the shapes they share with the live
 * commit graph, are in frame.tsx; the ink and the one animation are in
 * styles/figures.css.
 */
import type { ComponentType } from 'react';
import type { TutorialFigure } from '../../../types';
import { RemoteFigure, ConflictFigure } from './collab';
import { StackTraceFigure, TestRunFigure } from './debug';
import {
  BranchPointerFigure,
  CherryPickFigure,
  CommitChainFigure,
  MergeFigure,
  RebaseFigure,
  SquashFigure,
} from './history';
import { BisectFigure, ReflogFigure } from './recovery';
import { FilesystemFigure, RedirectFigure, SearchFigure } from './shell';
import { ResetModesFigure, RevertFigure, StashFigure, ThreeTreesFigure } from './trees';

export interface FigureProps {
  /** True while this figure's card is the one on screen; what starts the animation. */
  active: boolean;
}

export const FIGURES: Record<TutorialFigure, ComponentType<FigureProps>> = {
  filesystem: FilesystemFigure,
  redirect: RedirectFigure,
  search: SearchFigure,
  'three-trees': ThreeTreesFigure,
  'commit-chain': CommitChainFigure,
  'branch-pointer': BranchPointerFigure,
  'reset-modes': ResetModesFigure,
  revert: RevertFigure,
  remote: RemoteFigure,
  merge: MergeFigure,
  conflict: ConflictFigure,
  reflog: ReflogFigure,
  rebase: RebaseFigure,
  stash: StashFigure,
  'test-run': TestRunFigure,
  'stack-trace': StackTraceFigure,
  'cherry-pick': CherryPickFigure,
  bisect: BisectFigure,
  squash: SquashFigure,
};
